/* eslint-disable ember/no-runloop, warp-drive/no-legacy-request-patterns */
import Service, { service } from '@ember/service';
import { debounce } from '@ember/runloop';
import StudentReportState from '@linc-technologies/linced-common/enums/reports/student-report-state';
import { saveSection } from '../utils/reporting';

export default class StudentReportActionsService extends Service {
  // Services
  @service
  fetch;

  @service
  session;

  @service
  store;

  @service
  toaster;

  _buildUrl(reportId, action) {
    let adapter = this.store.adapterFor('reports/student-report');
    let host = adapter.host || '';
    return `${host}/${adapter.namespace}/student-reports/${reportId}/${action}`;
  }

  async _postAction(report, action, newState) {
    let url = this._buildUrl(report.get('id'), action);
    await this.fetch.post(url);
    report.set('state', newState);
  }

  async submit(report) {
    await this._postAction(
      report,
      'submit',
      StudentReportState.READY_TO_REVIEW,
    );
  }

  async approve(report) {
    await this._postAction(
      report,
      'approve',
      StudentReportState.READY_TO_PUBLISH,
    );
  }

  async publish(report) {
    await this._postAction(report, 'publish', StudentReportState.PUBLISHED);
  }

  async reopen(report) {
    await this._postAction(report, 'reopen', StudentReportState.DRAFT);
  }

  async void(report) {
    await this._postAction(report, 'void', StudentReportState.VOID);
  }

  // POST /student-reports/{id}/sync-template — migrate the report (and its
  // sections) in place to the current/newest template version. Returns the
  // updated record; push it into the store so the local copy reflects the new
  // template version. Report state (e.g. READY_TO_PUBLISH) is unchanged.
  async syncTemplate(report) {
    let url = this._buildUrl(report.get('id'), 'sync-template');
    let payload = await this.fetch.post(url);
    if (payload && payload.studentReport) {
      this.store.pushPayload('reports/student-report', payload);
    }
    return report;
  }

  // POST /student-reports/{id}/recreate — backend copies the source report
  // (and its sections) into a fresh DRAFT against the current template
  // version, and returns the new record. Used by the void & edit flow.
  async recreate(report) {
    let url = this._buildUrl(report.get('id'), 'recreate');
    let payload = await this.fetch.post(url);
    this.store.pushPayload('reports/student-report', payload);
    return this.store.peekRecord(
      'reports/student-report',
      payload.studentReport.id,
    );
  }

  // Resolve the current (latest) version of a report template via the
  // /current endpoint. Accepts a template record or an ID string. Reloads
  // the template so `next` reflects the server state — without this, a
  // locally-cached record loaded before a supersede landed would report
  // next: null and we'd return a stale template as "current". Returns the
  // reloaded template itself if it has no `next`; otherwise pushes the
  // current version into the store and returns it.
  async resolveCurrentTemplate(templateOrId) {
    let id =
      typeof templateOrId === 'string' ? templateOrId : templateOrId.get('id');
    let template = await this.store.findRecord('reports/report-template', id, {
      reload: true,
    });

    if (!template.get('next')) return template;

    let adapter = this.store.adapterFor('reports/report-template');
    let host = adapter.host || '';
    let url = `${host}/${adapter.namespace}/report-templates/${id}/current`;
    let payload = await this.fetch.request(url);
    this.store.pushPayload('reports/report-template', payload);
    return this.store.peekRecord(
      'reports/report-template',
      payload.reportTemplate.id,
    );
  }

  // Refresh the student reports list on the student controller
  async refreshStudentReports(studentCtrl, personId) {
    let reports = await this.store.query('reports/student-report', {
      studentId: personId,
      schoolId: this.session.activeSchool,
    });
    studentCtrl.set('studentReports', reports.slice());
  }

  // Prompt the browser to confirm tab close / hard reload while a long
  // operation is in flight. Returns a teardown fn that callers must invoke
  // in a finally. Intended to be installed once around a whole batch/flow
  // rather than per inner call.
  blockUnload() {
    let handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }

  // POSTs the draft with ?createSections=true so the backend atomically
  // creates the matching student-report-sections from the template and
  // sideloads them into the response. Sideloaded sections are normalized
  // into the store by ember-data automatically. Callers are responsible
  // for installing blockUnload around the whole operation.
  async createDraft({ student, template, periodIndex, year }) {
    let school =
      this.store.peekRecord('school', this.session.activeSchool) ||
      (await this.store.findRecord('school', this.session.activeSchool));
    let newReport = this.store.createRecord('reports/student-report', {
      schoolId: school,
      studentId: student,
      reportTemplateId: template,
      periodIndex: periodIndex,
      year: year,
      state: StudentReportState.DRAFT,
    });
    await newReport.save({ adapterOptions: { createSections: true } });
    return newReport;
  }

  // Append selected goals as a next-steps goals list on the given section.
  // Returns true if any changes were made, false otherwise.
  confirmAddGoals(section, selected) {
    if (!section || !selected || !selected.length) return false;
    let items = selected.map((g) => g.get('peopleDescription')).filter(Boolean);
    if (!items.length) return false;
    let html =
      '<ul>' +
      // Goal peopleDescription is stored HTML-encoded, so embed as-is —
      // escaping again would double-encode entities like &#39;.
      items.map((desc) => '<li>' + desc + '</li>').join('') +
      '</ul>';
    // Goals live in their own half of the next-steps block, so adding them
    // never touches the rubric-generated next steps.
    let existing = (section.get('nextStepsGoals') || '').trim();
    if (existing && existing.replace(/<[^>]*>/g, '').trim().length > 0) {
      section.set('nextStepsGoals', existing + html);
    } else {
      section.set('nextStepsGoals', html);
    }
    debounce(section, saveSection, section, this.toaster, 1000);
    return true;
  }
}
